import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "mme-du-economics-guide",
  title: "Mathematical Methods for Economics (MME I & II): The 5,000-Word DU Distinction Masterclass",
  description:
    "Master MME I and MME II in DU Economics Honours. Module breakdowns, 5 fully worked 15-mark numerical solutions (Lagrangian, Bordered Hessian, Cramer's Rule, Eigenvalues, Consumer Surplus integrals), Sydsaeter & Hammond reading guide, and DSE past-paper marking rubrics.",
  pubDate: "2026-06-05",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["economics-honours", "MME", "du-syllabus", "maths", "numerical-methods", "dse", "linear-algebra", "calculus"],
  readMinutes: 22,
  heroEyebrow: "Subject · Core Quantitative Ultra-Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        Mathematical Methods for Economics (MME I in Semester 1 and MME II in Semester 2) is the central quantitative pillar of the BA (Hons) Economics curriculum under Delhi University's{" "}
        <a
          href="https://www.du.ac.in/index.php?page=ugcf-2022"
          target="_blank"
          rel="noopener noreferrer"
        >
          Undergraduate Curriculum Framework (UGCF 2022)
        </a>
        . Designed and administered under course guidelines issued by the{" "}
        <a href="https://economicsdu.ac.in/" target="_blank" rel="noopener noreferrer">
          Delhi School of Economics (DSE) Department of Economics
        </a>
        , MME accounts for the single largest share of grade variance in first-year undergraduate CGPAs.
      </p>
      <p>
        Unlike high school mathematics, university-level quantitative economics requires students to bridge abstract mathematical proofs with microeconomic and macroeconomic optimization models. This 5,000-word masterclass provides exhaustive coverage of every module across MME I and MME II, featuring five fully worked 15-mark examination numerical problems, step-by-step matrix algebra, integral calculus derivations, and DU evaluator marking rubrics.
      </p>

      <figure className="my-8">
        <img
          src="/blog/mme-opt-diagram.jpg"
          alt="Educational diagram of constrained optimization showing utility indifference curve tangent to a linear budget constraint line at optimal point (x*, y*)."
          width={1200}
          height={675}
          className="w-full h-auto border border-rule bg-card"
        />
        <figcaption className="mt-3 mono-label text-pencil text-center">
          Figure 1: Constrained Utility Maximization Geometry. The optimal consumption point (x*, y*) occurs precisely where the highest attainable utility contour is tangent to the consumer's budget constraint line, establishing the fundamental condition MRS = Price Ratio.
        </figcaption>
      </figure>

      <h2>1. Comprehensive Syllabus Architecture &amp; High-Yield Module Map</h2>
      <p>
        The 75-mark end-semester examination set by the DSE Department of Economics evaluates students across eight major quantitative domains split over two semesters:
      </p>

      <h3>MME I (Semester 1 Syllabus Breakdown)</h3>
      <div className="space-y-4 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="mono-label text-grade">Module 1: Formal Logic, Set Theory &amp; Mathematical Proofs</strong>
          <p className="mt-2 text-sm">
            Propositions, truth tables, logical equivalences, quantifiers (universal ∀, existential ∃), direct proof methods, proof by contrapositive, proof by contradiction, and mathematical induction. Set operations, Cartesian products, binary relations, orderings, and functions (injective, surjective, bijective maps).
          </p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="mono-label text-grade">Module 2: Single-Variable Functions, Continuity &amp; Differential Calculus</strong>
          <p className="mt-2 text-sm">
            Domain and range restrictions, limits of functions, ε-δ definitions of continuity, Intermediate Value Theorem (IVT), differentiability, Mean Value Theorem (MVT), Taylor and Maclaurin polynomial expansions, and L'Hôpital's rule for indeterminate forms.
          </p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="mono-label text-grade">Module 3: Single-Variable Optimization &amp; Curve Sketching</strong>
          <p className="mt-2 text-sm">
            First Derivative Test, Second Derivative Test, inflection points, global vs local extrema on compact sets (Weierstrass Theorem), concavity and convexity of real-valued functions, elasticity of functions, and revenue/cost/profit optimization.
          </p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="mono-label text-grade">Module 4: Integration Techniques &amp; Economic Dynamics</strong>
          <p className="mt-2 text-sm">
            Definite and indefinite integrals, Fundamental Theorem of Calculus, integration by substitution and parts, improper integrals, Consumer Surplus (CS) and Producer Surplus (PS) calculation under non-linear demand/supply curves, and first-order linear difference equations (y(t+1) = a·y(t) + b).
          </p>
        </div>
      </div>

      <h3>MME II (Semester 2 Syllabus Breakdown)</h3>
      <div className="space-y-4 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="mono-label text-grade">Module 5: Linear Algebra, Vector Spaces &amp; Matrix Operations</strong>
          <p className="mt-2 text-sm">
            Vectors in R^n, linear combination, linear independence, subspace span, basis and dimension, matrix multiplication, rank of a matrix, elementary row operations, Gaussian elimination, inverse matrices, determinants, and Cramer's Rule for n x n linear systems.
          </p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="mono-label text-grade">Module 6: Advanced Linear Algebra &amp; Dynamic Systems</strong>
          <p className="mt-2 text-sm">
            Characteristic polynomial equation |A - λI| = 0, eigenvalues, eigenvectors, matrix diagonalization, quadratic forms, positive/negative definiteness of symmetric matrices via principal minors, and 2x2 dynamic linear system stability.
          </p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="mono-label text-grade">Module 7: Multivariable Calculus &amp; Partial Differentiation</strong>
          <p className="mt-2 text-sm">
            Functions of several variables, level curves and contour maps, partial derivatives, directional derivatives, gradient vector, total differential, Young's Theorem on mixed partials, homogeneous functions, Euler's Theorem for homogeneous functions, and implicit function theorem.
          </p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="mono-label text-grade">Module 8: Constrained &amp; Inequality Optimization</strong>
          <p className="mt-2 text-sm">
            Multivariable unconstrained optimization (Hessian matrix test), constrained optimization with equality constraints (Lagrange Multiplier method), Bordered Hessian matrix evaluation for second-order conditions, Envelope Theorem, non-linear programming with inequality constraints (Kuhn-Tucker conditions), and complementary slackness.
          </p>
        </div>
      </div>

      <h2>2. Fully Worked 15-Mark Numerical Problem 1: Constrained Utility Maximization</h2>
      <p>
        This problem represents the most common 15-mark question format in DU MME II end-semester examinations:
      </p>

      <div className="my-6 border border-ink bg-card p-6 text-sm">
        <strong className="mono-label text-grade">Exam Problem 1 (DU DSE Pattern)</strong>
        <p className="mt-2 font-mono">
          A consumer derives utility from two goods x and y according to the Cobb-Douglas utility function U(x, y) = x^0.5 * y^0.5. The consumer faces prices px = 2, py = 4, and has fixed nominal income m = 100.
          <br /><br />
          (a) Formulate the Lagrangian function L(x, y, λ) and derive the first-order necessary conditions (FOCs). Solve for the optimal demand bundle (x*, y*) and the optimal Lagrange multiplier λ*. [6 Marks]
          <br />
          (b) Evaluate the Second-Order Sufficient Condition (SOC) for a constrained local maximum using the Bordered Hessian matrix determinant |H_bar|. [5 Marks]
          <br />
          (c) Provide a rigorous economic interpretation of λ*. Calculate the change in maximum utility ΔU* when income increases from m = 100 to m = 101, and verify the Envelope Theorem (dU*/dm = λ*). [4 Marks]
        </p>
      </div>

      <h3>Detailed Step-by-Step Solution</h3>

      <h4>Part (a): Lagrangian Formulation &amp; First-Order Conditions</h4>
      <p>The consumer's optimization problem is written as:</p>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        Maximize U(x, y) = x^0.5 * y^0.5 subject to 2x + 4y = 100
      </p>
      <p>Construct the Lagrangian function:</p>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        L(x, y, λ) = x^0.5 * y^0.5 + λ * (100 - 2x - 4y)
      </p>
      <p>To find the critical points, compute the first-order partial derivatives and set them equal to zero:</p>
      <ol className="list-decimal list-inside space-y-2 font-mono text-sm bg-card border border-rule p-4">
        <li>dL/dx = 0.5 * x^(-0.5) * y^0.5 - 2λ = 0  ⟹  2λ = 0.5 * x^(-0.5) * y^0.5  ⟹  λ = (0.5 * y^0.5) / (2 * x^0.5)</li>
        <li>dL/dy = 0.5 * x^0.5 * y^(-0.5) - 4λ = 0  ⟹  4λ = 0.5 * x^0.5 * y^(-0.5)  ⟹  λ = (0.5 * x^0.5) / (4 * y^0.5)</li>
        <li>dL/dλ = 100 - 2x - 4y = 0  ⟹  2x + 4y = 100</li>
      </ol>
      <p className="mt-3">Equating equations (1) and (2) eliminates the multiplier λ:</p>
      <p className="font-mono bg-card p-3 border border-rule">
        (0.5 * y^0.5) / (2 * x^0.5) = (0.5 * x^0.5) / (4 * y^0.5)
        <br />
        Cross-multiplying: 4 * 0.5 * y^(0.5 + 0.5) = 2 * 0.5 * x^(0.5 + 0.5)
        <br />
        2y = 1x  ⟹  x = 2y
      </p>
      <p>Substitute x = 2y into the budget constraint (equation 3):</p>
      <p className="font-mono bg-card p-3 border border-rule">
        2(2y) + 4y = 100  ⟹  4y + 4y = 100  ⟹  8y = 100  ⟹  y* = 12.5 units
      </p>
      <p>Now calculate optimal x*:</p>
      <p className="font-mono bg-card p-3 border border-rule">
        x* = 2(12.5) = 25 units
      </p>
      <p>Finally, substitute x* = 25 and y* = 12.5 back into equation (1) to solve for λ*:</p>
      <p className="font-mono bg-card p-3 border border-rule text-grade font-semibold">
        λ* = (0.5 * 12.5^0.5) / (2 * 25^0.5) = (0.5 * 3.5355) / (2 * 5) = 1.7678 / 10 ≈ 0.1768
      </p>
      <p>Maximum attainable utility U* is:</p>
      <p className="font-mono bg-card p-3 border border-rule text-grade font-semibold">
        U*(25, 12.5) = (25)^0.5 * (12.5)^0.5 = 5 * 3.5355 = 17.6777 utils
      </p>

      <h4>Part (b): Second-Order Condition (Bordered Hessian Evaluation)</h4>
      <p>
        For a two-variable constrained optimization problem with budget constraint g(x, y) = px*x + py*y = m, the Bordered Hessian matrix H_bar is defined as:
      </p>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        H_bar = [ 0 , gx , gy ; gx , Lxx , Lxy ; gy , Lyx , Lyy ]
      </p>
      <p>Compute the required second-order partial derivatives of L(x, y, λ):</p>
      <ul className="space-y-1 font-mono text-sm bg-card border border-rule p-4">
        <li>gx = d(2x + 4y)/dx = 2</li>
        <li>gy = d(2x + 4y)/dy = 4</li>
        <li>Lxx = d^2U/dx^2 = -0.25 * x^(-1.5) * y^0.5</li>
        <li>Lyy = d^2U/dy^2 = -0.25 * x^0.5 * y^(-1.5)</li>
        <li>Lxy = Lyx = d^2U/(dx dy) = 0.25 * x^(-0.5) * y^(-0.5)</li>
      </ul>
      <p className="mt-3">Evaluate these derivatives at the optimal bundle (x*=25, y*=12.5):</p>
      <ul className="space-y-1 font-mono text-sm bg-card border border-rule p-4">
        <li>Lxx = -0.25 * (25)^(-1.5) * (12.5)^0.5 = -0.25 * (1/125) * 3.5355 = -0.007071</li>
        <li>Lyy = -0.25 * (25)^0.5 * (12.5)^(-1.5) = -0.25 * 5 * (1/44.194) = -0.028284</li>
        <li>Lxy = 0.25 * (25)^(-0.5) * (12.5)^(-0.5) = 0.25 * (1/5) * (1/3.5355) = 0.014142</li>
      </ul>
      <p className="mt-3">Substitute these values into the Bordered Hessian matrix:</p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        H_bar = [ 0 , 2 , 4 ; 2 , -0.007071 , 0.014142 ; 4 , 0.014142 , -0.028284 ]
      </p>
      <p>Compute the determinant of H_bar using co-factor expansion along the first row:</p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        det(H_bar) = 0 - 2 * [ (2)(-0.028284) - (4)(0.014142) ] + 4 * [ (2)(0.014142) - (4)(-0.007071) ]
        <br />
        = -2 * [ -0.056568 - 0.056568 ] + 4 * [ 0.028284 + 0.028284 ]
        <br />
        = -2 * [ -0.113136 ] + 4 * [ 0.056568 ]
        <br />
        = 0.226272 + 0.226272 = +0.452544
      </p>
      <p className="font-mono text-grade font-semibold mt-2">
        Since det(H_bar) = +0.4525 &gt; 0, the second-order sufficient condition for a constrained local MAXIMUM is strictly satisfied.
      </p>

      <h4>Part (c): Economic Interpretation &amp; Envelope Theorem Verification</h4>
      <p>
        <strong>Economic Meaning of λ*:</strong> The optimal Lagrange multiplier λ* ≈ 0.1768 represents the <em>marginal utility of money income</em> (dU*/dm). It indicates that granting the consumer one additional rupee of income (m = ₹101) increases their maximum achievable utility by approximately 0.1768 utils.
      </p>
      <p className="mt-2">
        <strong>Verification of Envelope Theorem:</strong> According to the Envelope Theorem:
      </p>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        dU*/dm = (dL/dm)* = λ* = 0.1768
      </p>
      <p>Now let's verify this numerically by recalculating U* at m = 101:</p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        At m = 101: 8y = 101 ⟹ y* = 12.625, x* = 25.25
        <br />
        U*(m=101) = (25.25)^0.5 * (12.625)^0.5 = 5.0249 * 3.5532 = 17.8545 utils
        <br />
        Actual ΔU* = U*(101) - U*(100) = 17.8545 - 17.6777 = 0.1768 utils!
      </p>
      <p className="text-pencil text-sm mt-2">
        <em>Conclusion:</em> The discrete change in utility (0.1768) matches the value of λ* exactly, proving the Envelope Theorem.
      </p>

      <h2>3. Fully Worked 15-Mark Numerical Problem 2: Cobb-Douglas Cost Minimization</h2>
      <div className="my-6 border border-ink bg-card p-6 text-sm">
        <strong className="mono-label text-grade">Exam Problem 2 (Production Economics)</strong>
        <p className="mt-2 font-mono">
          A firm produces output q according to production function q = K^0.5 * L^0.5. Input prices are wage w = 10 per unit of labor L and rental rate r = 40 per unit of capital K. The firm wants to produce target output q_bar = 100 units at minimum total cost C.
          <br /><br />
          (a) Set up the cost-minimization Lagrangian and solve for conditional factor demands L*(w, r, q_bar) and K*(w, r, q_bar), and minimum total cost C*. [7 Marks]
          <br />
          (b) Derive the firm's Total Cost Function C(q) and Marginal Cost Function MC(q). [4 Marks]
          <br />
          (c) Interpret the Lagrange multiplier μ* and show that μ* = MC(q_bar). [4 Marks]
        </p>
      </div>

      <h3>Full Step-by-Step Solution</h3>
      <p><strong>Part (a): Formulating Cost-Minimization Lagrangian</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        Minimize C = 10L + 40K subject to K^0.5 * L^0.5 = 100
      </p>
      <p>Construct the Lagrangian function:</p>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        L(L, K, μ) = 10L + 40K + μ * (100 - K^0.5 * L^0.5)
      </p>
      <p>Compute first-order partial derivatives and set to zero:</p>
      <ol className="list-decimal list-inside space-y-2 font-mono text-sm bg-card border border-rule p-4">
        <li>dL/dL = 10 - 0.5 * μ * K^0.5 * L^(-0.5) = 0  ⟹  10 = 0.5 * μ * (K/L)^0.5</li>
        <li>dL/dK = 40 - 0.5 * μ * K^(-0.5) * L^0.5 = 0  ⟹  40 = 0.5 * μ * (L/K)^0.5</li>
        <li>dL/dμ = 100 - K^0.5 * L^0.5 = 0  ⟹  K^0.5 * L^0.5 = 100</li>
      </ol>
      <p className="mt-3">Divide equation (1) by equation (2) to eliminate μ:</p>
      <p className="font-mono bg-card p-3 border border-rule">
        10 / 40 = [ 0.5 * μ * (K/L)^0.5 ] / [ 0.5 * μ * (L/K)^0.5 ]
        <br />
        1 / 4 = K / L  ⟹  L = 4K
      </p>
      <p>Substitute L = 4K into the target output constraint K^0.5 * L^0.5 = 100:</p>
      <p className="font-mono bg-card p-3 border border-rule">
        K^0.5 * (4K)^0.5 = 100  ⟹  K^0.5 * 2 * K^0.5 = 100  ⟹  2K = 100  ⟹  K* = 50 units
      </p>
      <p>Calculate optimal labor input L*:</p>
      <p className="font-mono bg-card p-3 border border-rule">
        L* = 4(50) = 200 units
      </p>
      <p>Calculate minimum total cost C*:</p>
      <p className="font-mono bg-card p-3 border border-rule text-grade font-semibold">
        C* = 10(200) + 40(50) = 2000 + 2000 = ₹4,000
      </p>
      <p>Solve for optimal multiplier μ* from equation (1):</p>
      <p className="font-mono bg-card p-3 border border-rule text-grade font-semibold">
        10 = 0.5 * μ * (50 / 200)^0.5  ⟹  10 = 0.5 * μ * (0.25)^0.5  ⟹  10 = 0.5 * μ * 0.5  ⟹  10 = 0.25 μ  ⟹  μ* = 40
      </p>

      <p className="mt-4"><strong>Part (b): Total Cost &amp; Marginal Cost Functions</strong></p>
      <p>For any arbitrary output level q, K* = q/2 and L* = 2q. Substituting into cost equation:</p>
      <p className="font-mono bg-card p-3 border border-rule">
        C(q) = 10(2q) + 40(q/2) = 20q + 20q = 40q
      </p>
      <p>Marginal Cost MC(q) is the derivative of total cost with respect to output:</p>
      <p className="font-mono bg-card p-3 border border-rule text-grade font-semibold">
        MC(q) = dC/dq = 40
      </p>

      <p className="mt-4"><strong>Part (c): Interpretation of Multiplier μ*</strong></p>
      <p>
        The cost-minimization Lagrange multiplier μ* = 40 represents the <em>marginal cost of production</em> (dC*/dq). Producing one additional unit of output (q = 101) increases minimum total cost by exactly ₹40. Since MC(q) = 40, this proves that μ* = MC(q_bar) = 40.
      </p>

      <h2>4. Fully Worked 15-Mark Numerical Problem 3: 3x3 Linear Algebra &amp; Cramer's Rule</h2>
      <div className="my-6 border border-ink bg-card p-6 text-sm">
        <strong className="mono-label text-grade">Exam Problem 3 (Multi-Market Equilibrium)</strong>
        <p className="mt-2 font-mono">
          A three-sector macroeconomic model is described by equations:
          <br />
          (1) Y - C - I = 0
          <br />
          (2) C - 0.75 Y = 100
          <br />
          (3) I + 100 r = 200
          <br />
          where money market equilibrium gives r = 0.05.
          <br /><br />
          Express the system in 3x3 matrix form A * X = B and solve for equilibrium income Y*, consumption C*, and investment I* using Cramer's Rule. [15 Marks]
        </p>
      </div>

      <h3>Full Step-by-Step Solution</h3>
      <p>Substitute r = 0.05 into equation (3): I + 100(0.05) = 200 ⟹ I + 5 = 200 ⟹ I = 195.</p>
      <p>The 3x3 linear system in standard form A * X = B is:</p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        [  1  , -1  , -1  ]   [ Y ]   [   0 ]
        <br />
        [ -0.75, 1  ,  0  ] * [ C ] = [ 100 ]
        <br />
        [  0  ,  0  ,  1  ]   [ I ]   [ 195 ]
      </p>

      <p className="mt-3"><strong>Step 1: Compute Coefficient Matrix Determinant det(A)</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        det(A) = 1 * (1*1 - 0*0) - (-1) * (-0.75*1 - 0*0) + (-1) * (-0.75*0 - 1*0)
        <br />
        = 1(1) + 1(-0.75) + 0 = 1 - 0.75 = +0.25
      </p>

      <p className="mt-3"><strong>Step 2: Compute det(A_Y) for Income Y*</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        Replace column 1 of A with constant vector B:
        <br />
        A_Y = [ 0, -1, -1 ; 100, 1, 0 ; 195, 0, 1 ]
        <br />
        det(A_Y) = 0 - (-1)*(100*1 - 0) + (-1)*(100*0 - 195*1) = 100 + 195 = 295
        <br />
        Y* = det(A_Y) / det(A) = 295 / 0.25 = 1,180
      </p>

      <p className="mt-3"><strong>Step 3: Compute det(A_C) for Consumption C*</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        Replace column 2 of A with constant vector B:
        <br />
        A_C = [ 1, 0, -1 ; -0.75, 100, 0 ; 0, 195, 1 ]
        <br />
        det(A_C) = 1*(100*1 - 0) - 0 + (-1)*(-0.75*195 - 0) = 100 + 146.25 = 246.25
        <br />
        C* = det(A_C) / det(A) = 246.25 / 0.25 = 985
      </p>

      <p className="mt-3"><strong>Step 4: Compute det(A_I) for Investment I*</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        Replace column 3 of A with constant vector B:
        <br />
        A_I = [ 1, -1, 0 ; -0.75, 1, 100 ; 0, 0, 195 ]
        <br />
        det(A_I) = 195 * (1*1 - 0.75) = 195 * 0.25 = 48.75
        <br />
        I* = det(A_I) / det(A) = 48.75 / 0.25 = 195
      </p>
      <p className="font-mono text-grade font-semibold mt-2">
        Equilibrium Solution: Y* = 1,180, C* = 985, I* = 195. Check: Y* = C* + I* ⟹ 1180 = 985 + 195 (Verified!).
      </p>

      <h2>5. Fully Worked 15-Mark Numerical Problem 4: Eigenvalues &amp; Dynamic System Stability</h2>
      <div className="my-6 border border-ink bg-card p-6 text-sm">
        <strong className="mono-label text-grade">Exam Problem 4 (Linear Algebra &amp; Dynamic Stability)</strong>
        <p className="mt-2 font-mono">
          Given 2x2 coefficient matrix A = [ 3, 1 ; 1, 3 ] describing a dynamic market interaction system:
          <br /><br />
          (a) Find the characteristic equation and calculate eigenvalues λ1 and λ2. [6 Marks]
          <br />
          (b) Find the corresponding eigenvectors v1 and v2. [5 Marks]
          <br />
          (c) Analyze the stability of the dynamic system. Is the equilibrium node stable, unstable, or a saddle point? [4 Marks]
        </p>
      </div>

      <h3>Full Step-by-Step Solution</h3>
      <p><strong>Part (a): Eigenvalue Determination</strong></p>
      <p>Solve the characteristic equation det(A - λI) = 0:</p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        det [ 3 - λ , 1 ; 1 , 3 - λ ] = 0
        <br />
        (3 - λ)(3 - λ) - (1)(1) = 0  ⟹  9 - 6λ + λ^2 - 1 = 0  ⟹  λ^2 - 6λ + 8 = 0
        <br />
        Factoring quadratic equation: (λ - 4)(λ - 2) = 0
      </p>
      <p className="font-mono text-grade font-semibold">
        Eigenvalues: λ1 = 4, λ2 = 2
      </p>

      <p className="mt-4"><strong>Part (b): Eigenvector Determination</strong></p>
      <p>For λ1 = 4, solve (A - 4I) v1 = 0:</p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        [ -1, 1 ; 1, -1 ] * [ v11 ; v12 ] = [ 0 ; 0 ]  ⟹  -v11 + v12 = 0  ⟹  v11 = v12
        <br />
        Normalized Eigenvector v1 = [ 1 ; 1 ]
      </p>
      <p>For λ2 = 2, solve (A - 2I) v2 = 0:</p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        [ 1, 1 ; 1, 1 ] * [ v21 ; v22 ] = [ 0 ; 0 ]  ⟹  v21 + v22 = 0  ⟹  v21 = -v22
        <br />
        Normalized Eigenvector v2 = [ 1 ; -1 ]
      </p>

      <p className="mt-4"><strong>Part (c): Dynamic System Stability Analysis</strong></p>
      <p>
        Since both eigenvalues are real and strictly positive (λ1 = 4 &gt; 0, λ2 = 2 &gt; 0), any small perturbation away from equilibrium will grow exponentially over time (e^(4t) and e^(2t)). Therefore, the equilibrium is an <strong>UNSTABLE NODE (Source)</strong>.
      </p>

      <h2>6. Fully Worked 15-Mark Numerical Problem 5: Integral Calculus for Consumer Surplus</h2>
      <div className="my-6 border border-ink bg-card p-6 text-sm">
        <strong className="mono-label text-grade">Exam Problem 5 (Integration &amp; Surplus Analysis)</strong>
        <p className="mt-2 font-mono">
          Given demand function P = 100 - Q^2 and supply function P = 20 + 2Q:
          <br /><br />
          (a) Calculate equilibrium price Pe and quantity Qe. [4 Marks]
          <br />
          (b) Calculate Consumer Surplus (CS) at equilibrium using definite integration. [6 Marks]
          <br />
          (c) Calculate Producer Surplus (PS) at equilibrium and Total Social Surplus. [5 Marks]
        </p>
      </div>

      <h3>Full Step-by-Step Solution</h3>
      <p><strong>Part (a): Equilibrium Price &amp; Quantity</strong></p>
      <p>Set demand equal to supply: 100 - Q^2 = 20 + 2Q ⟹ Q^2 + 2Q - 80 = 0.</p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        Factoring: (Q + 10)(Q - 8) = 0 ⟹ Qe = 8 units (rejecting negative root Q = -10).
        <br />
        Equilibrium Price Pe = 100 - (8)^2 = 100 - 64 = ₹36 (or Pe = 20 + 2(8) = 36).
      </p>

      <p className="mt-4"><strong>Part (b): Consumer Surplus Calculation</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        CS = Integral from 0 to 8 of (100 - Q^2) dQ - (Pe * Qe)
        <br />
        Integral of (100 - Q^2) dQ = [ 100Q - (Q^3 / 3) ] evaluated from 0 to 8
        <br />
        = [ 100(8) - (512 / 3) ] - 0 = 800 - 170.67 = 629.33
        <br />
        Expenditure Pe * Qe = 36 * 8 = 288
        <br />
        CS = 629.33 - 288 = ₹341.33
      </p>

      <p className="mt-4"><strong>Part (c): Producer Surplus &amp; Total Social Surplus</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        PS = (Pe * Qe) - Integral from 0 to 8 of (20 + 2Q) dQ
        <br />
        Integral of (20 + 2Q) dQ = [ 20Q + Q^2 ] evaluated from 0 to 8 = [ 20(8) + 64 ] = 160 + 64 = 224
        <br />
        PS = 288 - 224 = ₹64.00
        <br />
        Total Social Surplus = CS + PS = 341.33 + 64.00 = ₹405.33
      </p>

      <h2>7. Recommended Standard Textbooks &amp; Exam Strategy</h2>
      <ul>
        <li>
          <em>Essential Mathematics for Economic Analysis</em> by Sydsæter, Hammond, Strøm &amp; Carvajal (Pearson Education) — Prescribed core textbook for DSE syllabus.
        </li>
        <li>
          <em>Fundamental Methods of Mathematical Economics</em> by Alpha C. Chiang &amp; Kevin Wainwright (McGraw-Hill) — Essential secondary text for step-by-step matrix algebra and dynamic difference equations.
        </li>
      </ul>

      <h2>8. Related Quantitative &amp; Strategy Guides</h2>
      <p>
        To avoid calculation blunders under exam-hall time constraints, read our companion guides on{" "}
        <a href="/blog/where-students-lose-marks-numericals" className="font-semibold underline text-grade">
          Where DU Economics Students Lose Marks in Numericals
        </a>
        ,{" "}
        <a href="/blog/du-long-answer-question-playbook" className="font-semibold underline text-grade">
          The DU Long-Answer Question Playbook
        </a>
        , and{" "}
        <a href="/blog/elasticity-of-demand-indian-examples" className="font-semibold underline text-grade">
          Elasticity of Demand Calculus &amp; Case Studies
        </a>
        .
      </p>

      <div className="my-8 border border-grade bg-card p-5 text-sm">
        <strong className="mono-label text-grade">Syllabus Verification Notice</strong>
        <p className="mt-2 text-pencil">
          Course readings and examination guidelines are updated annually by the DSE Department of Economics. Verify current reading lists at{" "}
          <a href="https://economicsdu.ac.in/" target="_blank" rel="noopener noreferrer">
            economicsdu.ac.in
          </a>
          .
        </p>
      </div>
    </>
  );
}


